import type { UseComboboxState, UseComboboxStateChangeOptions } from 'downshift'
import { useCombobox } from 'downshift'
import take from 'lodash/take'
import unionBy from 'lodash/unionBy'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Scrollbars } from 'react-custom-scrollbars'

import { useLocalStorage } from '@procsea/common/hooks'

import type { Width } from '../../../themes'
import { Card } from '../../surfaces'
import Input from '../Input/Input'
import type { FormOnChange, InputProps } from '../interfaces'
import {
  RecentQueriesTitle,
  SearchBarContainer,
  SearchbarMenu,
  StackContainer,
} from './Searchbar.styles'
import type { SearchOption } from './Searchbar.utils'
import { getHighlightedSuggestions } from './Searchbar.utils'
import SearchbarItem from './SearchbarItem/SearchbarItem'

export const RECENT_QUERIES_TITLE = 'Recent searches'

export interface SearchbarProps extends InputProps<string> {
  debounce?: number
  options?: any[]
  recentQueriesTitle?: string
  maxRecentQueries?: number
  minSearchLength?: number
  recentQueriesUniqueId?: string
  onSelectedItemChange?: FormOnChange<string>
  width?: Width
}

interface CustomUseComboboxState<Item> extends UseComboboxState<Item> {
  previousType?: UseComboboxStateChangeOptions<Item>['type']
}

const stateReducer = <Item,>(
  state: CustomUseComboboxState<Item>,
  actionAndChanges: UseComboboxStateChangeOptions<Item>
): Partial<CustomUseComboboxState<Item>> => {
  const { type, changes } = actionAndChanges
  switch (type) {
    case useCombobox.stateChangeTypes.InputChange:
      return {
        ...changes,
        selectedItem: null,
        previousType: type,
      }
    case useCombobox.stateChangeTypes.FunctionOpenMenu:
      return {
        ...changes,
        isOpen: state.previousType !== useCombobox.stateChangeTypes.ItemClick,
        previousType: type,
      }
    default:
      return { ...changes, previousType: type }
  }
}

const itemToString = (item: SearchOption | null) => (item ? item.name : '')

const Searchbar = ({
  'data-e2e': dataE2e,
  autofocus = false,
  className,
  debounce = 1000,
  fluid = false,
  helpText,
  label,
  maxRecentQueries = 3,
  minSearchLength = 3,
  name,
  onChange,
  onSelectedItemChange,
  options = [],
  placeholder,
  recentQueriesTitle = RECENT_QUERIES_TITLE,
  recentQueriesUniqueId = '',
  value = '',
  width = '35rem',
}: SearchbarProps) => {
  const [timeoutID, setTimeoutID] = useState<number>()
  const [recentQueries, setRecentQueries] = useLocalStorage<SearchOption[]>(
    recentQueriesUniqueId,
    []
  )
  const [items, setItems] = useState<SearchOption[]>(
    recentQueriesUniqueId ? recentQueries : options
  )

  const handleChange: FormOnChange<string> = useCallback(
    data => {
      if (timeoutID) clearTimeout(timeoutID)

      if (typeof onChange === 'function') {
        setTimeoutID(prevState => {
          if (prevState) clearTimeout(prevState)

          return window.setTimeout(() => {
            if (data.value.length === 0 || data.value.length >= minSearchLength) {
              onChange(data)
            }
          }, debounce)
        })
      }
    },
    [onChange, debounce, minSearchLength]
  )

  const {
    getComboboxProps,
    getInputProps,
    getItemProps,
    getMenuProps,
    highlightedIndex,
    inputValue,
    isOpen,
    openMenu,
  } = useCombobox({
    initialInputValue: value,
    inputValue: value,
    items,
    itemToString,
    stateReducer,
    onInputValueChange: ({ inputValue = '' }) => {
      if (recentQueriesUniqueId && !inputValue && recentQueries.length) {
        setItems(recentQueries)
      } else {
        setItems(getHighlightedSuggestions(options, inputValue))
      }

      handleChange({ name, value: inputValue })
    },
    onSelectedItemChange: ({ selectedItem }) => {
      if (recentQueriesUniqueId && selectedItem) {
        const { name, id } = selectedItem
        setRecentQueries(take(unionBy([{ name, id }], recentQueries, 'id'), maxRecentQueries))
      }

      onSelectedItemChange?.({ name, value: selectedItem?.name || '' })
    },
  })

  const isShowingRecentQueries = useMemo(
    () => recentQueriesUniqueId && !inputValue && recentQueries.length > 0,
    [recentQueriesUniqueId, inputValue, recentQueries.length]
  )

  useEffect(() => {
    setItems(getHighlightedSuggestions(options, inputValue))
  }, [JSON.stringify(options)])

  const handleFocus = useCallback(() => {
    if (isShowingRecentQueries) {
      setItems(recentQueries)
      openMenu()
    } else if (inputValue.length) {
      openMenu()
    }
  }, [recentQueries, isShowingRecentQueries, openMenu, inputValue])

  return (
    <StackContainer fluid={fluid} className={className} width={width}>
      <div {...getComboboxProps()}>
        <Input
          autofocus={autofocus}
          data-e2e={dataE2e}
          helpText={helpText}
          icon="search"
          label={label}
          name={name}
          placeholder={placeholder}
          rounded
          search
          {...getInputProps({ onFocus: handleFocus })}
        />
      </div>
      <SearchBarContainer>
        <SearchbarMenu {...getMenuProps()}>
          {isOpen && items.length > 0 && (
            <Card shadow="middle">
              <Scrollbars autoHeight autoHeightMax={214}>
                {isShowingRecentQueries && (
                  <RecentQueriesTitle variant="neutral" bold fontCase="uppercase">
                    {recentQueriesTitle}
                  </RecentQueriesTitle>
                )}

                {items.map((item, index) => (
                  <SearchbarItem
                    highlighted={highlightedIndex === index}
                    icon={isShowingRecentQueries && 'history'}
                    item={item}
                    key={`${item.id}${index}`}
                    {...getItemProps({ item, index })}
                  />
                ))}
              </Scrollbars>
            </Card>
          )}
        </SearchbarMenu>
      </SearchBarContainer>
    </StackContainer>
  )
}

export default Searchbar
